// Pure decision math for the deck swipe pager. No DOM, no timers — every
// threshold that decides how the gesture *feels* lives here so it can be
// unit-tested, since the feel itself can only be judged in a real browser.

export const SLOP = 8;               // px of movement before the axis is decided
export const ANGLE_TAN = 0.577;      // tan(30deg) — the horizontal cone half-angle
export const COMMIT_RATIO = 0.2;     // fraction of the width that locks the page in
export const EDGE_RESISTANCE = 0.3;  // rubber-band factor past the first/last page
export const EDGE_MAX_RATIO = 0.12;  // how far a rubber-band may travel at an end

// Decides, once per gesture, whether the pager owns this drag or the editor
// does. 'v' is terminal: the gesture is handed to the editor for text selection
// and vertical scrolling and never taken back, so a drag that starts as a
// selection can't turn into a page halfway through.
export function resolveAxis(dx, dy, slop = SLOP) {
  if (Math.hypot(dx, dy) < slop) return null;
  return Math.abs(dy) <= ANGLE_TAN * Math.abs(dx) ? 'h' : 'v';
}

export function applyResistance(dx, atEdge) {
  return atEdge ? dx * EDGE_RESISTANCE : dx;
}

// A drag can never expose more than the one neighbouring note. Past an end
// there is nothing to expose at all, so the rubber-band is held to a short
// travel rather than dragging a blank surface into view.
export function clampDrag(x, width, atEdge) {
  const limit = atEdge ? width * EDGE_MAX_RATIO : width;
  return Math.max(-limit, Math.min(limit, x));
}

// The page locks in the moment the note passes this much of the screen — the
// swipe then completes on its own, without waiting for the finger to lift.
// Below it the gesture always returns; there is no velocity escape hatch, so
// the outcome is a function of distance alone and never a surprise.
export function hasReachedCommit(x, width) {
  return width > 0 && Math.abs(x) >= width * COMMIT_RATIO;
}

// Trackpads report DOM_DELTA_PIXEL, but mice and some drivers report lines or
// pages. Normalising here keeps the accumulator in one unit.
export function normalizeWheelDelta(delta, deltaMode, pageSize) {
  if (deltaMode === 1) return delta * 16;
  if (deltaMode === 2) return delta * pageSize;
  return delta;
}

export const COMMIT_MS = 320;
export const CHAIN_MS = 190;        // a page that follows hard on another's heels
export const CHAIN_WINDOW_MS = 700; // how recently the last page must have landed
export const CANCEL_MS = 220;
export const CANCEL_MIN_MS = 140;

// Someone paging quickly is telling you they want to get somewhere, not to
// admire the transition. Pages that follow closely on one another shorten, so
// rapid swiping accelerates with the user instead of queueing behind a fixed
// duration.
export function commitDuration(msSinceLastCommit) {
  return msSinceLastCommit < CHAIN_WINDOW_MS ? CHAIN_MS : COMMIT_MS;
}

// Returning scales with how far the drag actually got, so nudging a few pixels
// doesn't take as long as coming back from the commit threshold.
export function cancelDuration(travelled, width) {
  const span = width * COMMIT_RATIO;
  if (span <= 0) return CANCEL_MIN_MS;
  return Math.max(CANCEL_MIN_MS, Math.round(CANCEL_MS * Math.min(1, Math.abs(travelled) / span)));
}
